import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Thermometer,
  FileText,
  Package,
  Hash,
  ClipboardList,
  Link as LinkIcon,
  Printer,
  Search,
  Calendar,
  Info,
  Upload,
  Camera,
  Trash2,
  Snowflake,
  Users,
  Timer,
  Zap,
  Save,
  RefreshCw,
  File,
  Eye,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Page from "../../components/Page";

function todayIso() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function autoLotto() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `ABB-${yyyy}${mm}${dd}-${hh}${mi}${ss}`;
}

function isoToLocalDatetimeValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

function localDatetimeValueToIso(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

function toSqlDatetime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

function deadlineFrom(isoStart, trattamento) {
  if (!isoStart) return null;
  const base = new Date(isoStart).getTime();
  if (!Number.isFinite(base)) return null;
  const addMs = trattamento === "congelare" ? 4 * 3600_000 : 2 * 3600_000;
  return new Date(base + addMs);
}

function fmtDeadline(isoStart, trattamento) {
  const dl = deadlineFrom(isoStart, trattamento);
  return dl ? dl.toLocaleString("it-IT") : "-";
}

const TEMP_PRESETS = [
  { label: "Refrigerato", value: "4.0" },
  { label: "Ambiente", value: "20.0" },
  { label: "Caldo", value: "65.0" },
  { label: "Surgelato", value: "-18.0" },
];

async function apiGetJSON(url) {
  const r = await fetch(url, { credentials: "include" });
  const j = await r.json().catch(() => null);
  if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
  return j;
}

async function apiPostJSON(url, body) {
  const r = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const j = await r.json().catch(() => null);
  const ok = !!(j && (j.ok === true || j.success === true));
  if (!r.ok || !ok) throw new Error(j?.error || `HTTP ${r.status}`);
  return j;
}

function snapshotAvg(snapshot) {
  const v = Number(snapshot?.avg);
  return Number.isFinite(v) ? v : "";
}

function snapshotPoints(snapshot) {
  if (!snapshot) return [];
  return [snapshot.point_1, snapshot.point_2, snapshot.point_3].map((v, idx) => ({
    label: `P${idx + 1}`,
    value: v === null || v === undefined || v === "" ? null : Number(v),
  }));
}

function SnapshotPreview({ snapshot }) {
  if (!snapshot) return null;
  const points = snapshotPoints(snapshot);
  return (
    <div className="mt-3 grid grid-cols-3 gap-2">
      {points.map((p) => (
        <div key={p.label} className="rounded-[14px] border border-slate-100 bg-white px-3 py-2">
          <div className="text-[10px] font-bold uppercase text-slate-400">{p.label}</div>
          <div className="text-sm font-bold text-slate-800">
            {p.value === null || Number.isNaN(p.value) ? "--" : `${p.value.toFixed(1)} C`}
          </div>
        </div>
      ))}
    </div>
  );
}

async function uploadLabelPhotos(abbattimentoId, files) {
  if (!abbattimentoId || !files?.length) return { ok: true, uploaded: 0 };

  const fd = new FormData();
  fd.append("abbattimento_id", String(abbattimentoId));
  files.forEach((f) => fd.append("images[]", f));

  const r = await fetch("/api/abbattimenti_upload_labels.php", {
    method: "POST",
    credentials: "include",
    body: fd,
  });

  const j = await r.json().catch(() => null);
  if (!r.ok || !j?.ok) {
    throw new Error(j?.error || `Upload foto fallito (${r.status})`);
  }
  return j;
}

function cn(...arr) {
  return arr.filter(Boolean).join(" ");
}

function stepIconByKey(key) {
  switch (key) {
    case "trattamento":
      return Thermometer;
    case "docs":
      return FileText;
    case "fornitore":
      return Users;
    case "prodotti":
      return Package;
    case "lotto":
      return Hash;
    case "inizio":
      return Timer;
    case "abbattitore":
      return Zap;
    default:
      return FileText;
  }
}

function FieldLabel({ children }) {
  return (
    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 ml-1">
      {children}
    </label>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={cn(
        "w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-[16px] text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71]/20 focus:border-[#003B71] transition-all",
        className
      )}
    />
  );
}

function Card({ children }) {
  return (
    <div className="bg-white rounded-[24px] shadow-sm border border-slate-100 p-6 md:p-8 min-h-[420px] flex flex-col relative overflow-hidden transition-all">
      {children}
    </div>
  );
}

function OptionCard({ active, onClick, icon, title, desc, activeColor = "blue" }) {
  const isGreen = activeColor === "green";

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-full p-6 rounded-[24px] border-2 hover:bg-slate-50 transition-all flex flex-col gap-4 shadow-sm hover:shadow-md text-left",
        active
          ? isGreen
            ? "border-emerald-600 bg-emerald-50"
            : "border-[#003B71] bg-[#F0F7FF]"
          : "border-slate-100"
      )}
    >
      <div className="flex justify-between items-start">
        <div
          className={cn(
            "w-14 h-14 rounded-2xl bg-white border border-slate-100 flex items-center justify-center shadow-sm",
            isGreen ? "text-emerald-600" : "text-[#003B71]"
          )}
        >
          {icon}
        </div>

        <div
          className={cn(
            "w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all",
            active
              ? isGreen
                ? "border-emerald-600 bg-emerald-600"
                : "border-[#003B71] bg-[#003B71]"
              : "border-slate-300 bg-white"
          )}
        >
          <Check className={cn("w-3 h-3 text-white", active ? "opacity-100" : "opacity-0")} />
        </div>
      </div>

      <div>
        <h4 className="text-xl font-bold text-slate-800 mb-1">{title}</h4>
        <p className="text-sm text-slate-500 font-medium">{desc}</p>
      </div>
    </button>
  );
}

function Stepper({ steps, step, setStep, stepperRef, mobileProgress }) {
  return (
    <div className="relative sticky top-0 md:top-auto z-30 bg-[#F6F8FA]/95 md:bg-transparent backdrop-blur-md md:backdrop-blur-none -mx-4 px-4 md:mx-0 md:px-0 py-2 md:py-0 border-b border-slate-200/60 md:border-0">
      <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-[#F6F8FA] to-transparent z-10 pointer-events-none md:hidden" />
      <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-[#F6F8FA] to-transparent z-10 pointer-events-none md:hidden" />

      <div
        ref={stepperRef}
        className="w-full overflow-x-auto no-scrollbar py-2 scroll-smooth"
      >
        <div className="flex items-center min-w-max px-2 md:px-0">
          {steps.map((s, i) => {
            const done = i < step;
            const active = i === step;
            const Icon = stepIconByKey(s.key);

            return (
              <React.Fragment key={s.key}>
                <button
                  type="button"
                  data-step={i}
                  onClick={() => {
                    if (i <= step) setStep(i);
                  }}
                  className="flex flex-col items-center gap-2 relative z-10 min-w-[84px]"
                >
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                      done && "bg-emerald-600 border-emerald-600 text-white shadow-sm",
                      active &&
                      "bg-[#B95DEE] border-[#B95DEE] text-white shadow-md ring-4 ring-[#B95DEE]/20 scale-110",
                      !done && !active && "bg-white border-slate-200 text-slate-300"
                    )}
                  >
                    {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>

                  <span
                    className={cn(
                      "text-xs text-center transition-colors",
                      done && "text-emerald-700 font-bold",
                      active && "text-slate-800 font-bold",
                      !done && !active && "text-slate-400 font-medium"
                    )}
                  >
                    {s.label}
                  </span>
                </button>

                {i < steps.length - 1 && (
                  <div
                    className={cn(
                      "w-8 md:w-12 h-1 rounded-full mx-1 transition-colors duration-500",
                      i < step ? "bg-emerald-600" : "bg-slate-200"
                    )}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="h-1 w-full bg-slate-200 mt-1 md:hidden">
        <div
          className="h-full bg-[#B95DEE] transition-all duration-500 rounded-r-full"
          style={{ width: mobileProgress }}
        />
      </div>
    </div>
  );
}

export default function Abbattimenti({ onClose }) {
  const navigate = useNavigate();
  const stepperRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const close = () => {
    if (onClose) onClose();
    else navigate("/");
  };

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cryoDevices, setCryoDevices] = useState([]);
  const [cryoLoading, setCryoLoading] = useState(false);
  const [cryoError, setCryoError] = useState("");

  const [docQuery, setDocQuery] = useState("");
  const [docRows, setDocRows] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState("");
  const [previewDdt, setPreviewDdt] = useState(null);

  const [fornQuery, setFornQuery] = useState("");
  const [fornItems, setFornItems] = useState([]);
  const [fornOpen, setFornOpen] = useState(false);
  const [fornLoading, setFornLoading] = useState(false);

  const [result, setResult] = useState(null);

  const [form, setForm] = useState(() => {
    const nowIso = new Date().toISOString();
    return {
      trattamento: "",
      data_abbattimento: todayIso(),
      nome_prodotto: "",
      fornitore: "",
      fornitore_id: "",

      document_ids: [],
      prodotti: [{ nome: "", quantita: "", note: "" }],
      lotto: autoLotto(),

      inizio_iso: nowIso,
      temp_inizio: "",
      data_scadenza_prodotto: "",

      abbattitore_id: "",
      cryo_device_id: "",
      cryo_mac_address: "",
      cryo_monitoring_enabled: false,
      cryo_snapshot_start: null,

      source_mode: "both",
      label_photos: [],
    };
  });

  const steps = useMemo(
    () => [
      { key: "trattamento", label: "Trattamento" },
      { key: "docs", label: "Documenti" },
      { key: "fornitore", label: "Fornitore" },
      { key: "prodotti", label: "Prodotti" },
      { key: "lotto", label: "Lotto" },
      { key: "inizio", label: "Avvio" },
      { key: "abbattitore", label: "Abbattitore" },
    ],
    []
  );

  const mobileProgress = `${((step + 1) / steps.length) * 100}%`;

  useEffect(() => {
    const el = stepperRef.current?.querySelector(`[data-step="${step}"]`);
    if (el && stepperRef.current) {
      const container = stepperRef.current;
      const left = el.offsetLeft - container.offsetWidth / 2 + el.offsetWidth / 2;
      container.scrollTo({ left, behavior: "smooth" });
    }
  }, [step]);

  useEffect(() => {
    if (step !== 1) return;
    let stop = false;

    async function load() {
      try {
        setDocLoading(true);
        setDocError("");
        const q = encodeURIComponent(docQuery.trim());
        const url = `/api/etichette_get_ddt.php?with_file=1${q ? `&q=${q}` : ""}`;
        const rows = await apiGetJSON(url);
        if (!stop) setDocRows(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (!stop) setDocError(String(e.message || e));
      } finally {
        if (!stop) setDocLoading(false);
      }
    }

    load();
    return () => {
      stop = true;
    };
  }, [step, docQuery]);

  useEffect(() => {
    let stop = false;
    async function loadCryo() {
      try {
        setCryoLoading(true);
        setCryoError("");
        const j = await apiGetJSON("/api/cryo_abbattimenti_list.php");
        if (!stop) setCryoDevices(Array.isArray(j.items) ? j.items : []);
      } catch (e) {
        if (!stop) setCryoError(String(e.message || e));
      } finally {
        if (!stop) setCryoLoading(false);
      }
    }
    loadCryo();
    return () => {
      stop = true;
    };
  }, []);

  useEffect(() => {
    const q = fornQuery.trim();

    if (q.length < 2) {
      setFornItems([]);
      setFornOpen(false);
      return;
    }

    const ac = new AbortController();
    const t = setTimeout(async () => {
      try {
        setFornLoading(true);
        const r = await fetch(`/api/fornitori_search.php?q=${encodeURIComponent(q)}`, {
          credentials: "include",
          signal: ac.signal,
        });
        const js = await r.json().catch(() => null);
        if (!r.ok) throw new Error(js?.message || js?.error || `HTTP ${r.status}`);
        const items = (Array.isArray(js?.items) ? js.items : Array.isArray(js) ? js : [])
          .map((x, idx) => ({
            id: x?.id ?? idx,
            nome: x?.nome || x?.ragione_sociale || "",
          }))
          .filter((x) => x.nome.trim().length > 0);
        setFornItems(items);
        setFornOpen(items.length > 0);
      } catch {
        if (!ac.signal.aborted) {
          setFornItems([]);
          setFornOpen(false);
        }
      } finally {
        if (!ac.signal.aborted) setFornLoading(false);
      }
    }, 250);

    return () => {
      ac.abort();
      clearTimeout(t);
    };
  }, [fornQuery]);

  useEffect(() => {
    return () => {
      (form.label_photos || []).forEach((p) => {
        if (p.preview) URL.revokeObjectURL(p.preview);
      });
    };
  }, [form.label_photos]);

  function addPhotoFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const mapped = files.map((file, idx) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${idx}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type?.startsWith("image/") ? URL.createObjectURL(file) : "",
    }));

    setForm((f) => ({
      ...f,
      label_photos: [...f.label_photos, ...mapped],
    }));
  }

  function removePhoto(id) {
    setForm((f) => {
      const target = f.label_photos.find((x) => x.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return {
        ...f,
        label_photos: f.label_photos.filter((x) => x.id !== id),
      };
    });
  }

  function validateCurrent() {
    switch (step) {
      case 0:
        return form.trattamento === "congelare" || form.trattamento === "refrigerare";
      case 1: {
        if (form.source_mode === "ddt") return form.document_ids.length > 0;
        if (form.source_mode === "photos") return form.label_photos.length > 0;
        return form.document_ids.length > 0 || form.label_photos.length > 0;
      }
      case 2:
        return form.fornitore.trim().length > 0;
      case 3:
        return (form.prodotti || []).some((p) => (p.nome || "").trim().length > 0);
      case 4:
        return form.lotto.trim().length > 0;
      case 5:
        return (
          !!form.inizio_iso &&
          form.temp_inizio !== "" &&
          !Number.isNaN(Number(form.temp_inizio))
        );
      case 6:
        return true;
      default:
        return true;
    }
  }

  function handleBack() {
    setStep((s) => Math.max(0, s - 1));
  }

  async function captureCryoStart(deviceId) {
    if (!deviceId) return;
    try {
      setCryoLoading(true);
      const j = await apiGetJSON(`/api/cryo_abbattimenti_snapshot.php?device_id=${encodeURIComponent(deviceId)}`);
      const snapshot = j.snapshot || null;
      const avg = snapshotAvg(snapshot);
      setForm((f) => ({
        ...f,
        cryo_snapshot_start: snapshot,
        cryo_mac_address: snapshot?.mac_address || f.cryo_mac_address,
        temp_inizio: avg !== "" ? String(avg) : f.temp_inizio,
      }));
    } catch (e) {
      alert(`Lettura Cryo non disponibile: ${String(e.message || e)}`);
    } finally {
      setCryoLoading(false);
    }
  }

  async function handleNext() {
    if (!validateCurrent()) return;

    if (step < steps.length - 1) {
      setStep((s) => Math.min(steps.length - 1, s + 1));
      return;
    }

    setLoading(true);
    try {
      const prodottiPuliti = (form.prodotti || [])
        .map((p) => ({
          nome: (p.nome || "").trim(),
          quantita: (p.quantita || "").trim(),
          note: (p.note || "").trim(),
        }))
        .filter((p) => p.nome.length > 0);

      const nomeProd = prodottiPuliti
        .map((p) => `${p.nome}${p.quantita ? ` - ${p.quantita}` : ""}`)
        .join("; ");

      const payload = {
        mode: "save",
        trattamento: form.trattamento,
        data_abbattimento: form.data_abbattimento,
        nome_prodotto: nomeProd,
        fornitore: form.fornitore.trim(),
        fornitore_id: form.fornitore_id ? Number(form.fornitore_id) : null,
        lotto: form.lotto.trim(),
        ddt_ids: form.document_ids,
        prodotti: prodottiPuliti,
        abbattitore_id: form.abbattitore_id ? Number(form.abbattitore_id) : null,
        cryo_device_id: form.cryo_device_id ? Number(form.cryo_device_id) : null,
        cryo_mac_address: form.cryo_mac_address || null,
        cryo_monitoring_enabled: !!form.cryo_monitoring_enabled,
        cryo_snapshot_start: form.cryo_snapshot_start,
        cryo_started_at: form.cryo_snapshot_start?.captured_at || null,
        inizio: toSqlDatetime(form.inizio_iso),
        temp_inizio: Number(form.temp_inizio),
        data_scadenza_prodotto: form.data_scadenza_prodotto,
        source_mode: form.source_mode,
      };

      const j = await apiPostJSON("/api/abbattimenti_save.php", payload);
      const newId = j.id;

      if (newId && form.label_photos.length > 0) {
        await uploadLabelPhotos(
          newId,
          form.label_photos.map((x) => x.file)
        );
      }

      const token = j.public_token || j.token || null;
      setResult({
        ok: true,
        id: newId,
        lotto: j.lotto || payload.lotto,
        docs_url: token ? `/api/abbattimenti_docs.php?t=${encodeURIComponent(token)}` : null,
      });
    } catch (e) {
      alert(`Errore salvataggio: ${String(e.message || e)}`);
    } finally {
      setLoading(false);
    }
  }

  if (result?.ok) {
    return (
      <Page title="Abbattimenti" back>
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-8 space-y-6">
          <div id="main-title-area">
            <h2 className="text-3xl md:text-4xl text-slate-800 font-light tracking-tight">
              Registro Abbattimenti
            </h2>
            <p className="text-slate-400 text-sm mt-1 font-light">Operazione completata correttamente.</p>
          </div>

          <Card>
            <div className="h-full flex flex-col justify-center items-center text-center py-8">
              <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-6 shadow-sm">
                <Check className="w-10 h-10 text-emerald-600" />
              </div>

              <h2 className="text-3xl font-bold text-slate-800 mb-2">Registrazione salvata</h2>

              <div className="mt-4 text-sm text-slate-700 space-y-2">
                <div>
                  ID: <span className="font-mono font-semibold">{result.id}</span>
                </div>
                <div>
                  Lotto: <span className="font-mono font-semibold">{result.lotto}</span>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap justify-center gap-3">
                {result.docs_url && (
                  <a
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-[18px] bg-slate-900 text-white shadow hover:bg-slate-800"
                    href={result.docs_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LinkIcon className="w-4 h-4" />
                    Apri documenti
                  </a>
                )}

                {result.id && (
                  <a
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-[18px] bg-[#003B71] text-white shadow hover:bg-[#002B5B]"
                    href={`/api/abbattimenti_pdf.php?id=${encodeURIComponent(result.id)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Printer className="w-4 h-4" />
                    Stampa etichetta
                  </a>
                )}

                <button
                  type="button"
                  onClick={close}
                  className="px-5 py-3 rounded-[18px] border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </Card>
        </div>
      </Page>
    );
  }

  return (
    <Page title="Abbattimenti" back>
      {previewDdt && (
        <div
          className="fixed inset-0 z-[100] bg-slate-950/70 backdrop-blur-sm p-3 md:p-8 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={`Anteprima DDT ${previewDdt.ddt || previewDdt.numero || `#${previewDdt.id}`}`}
        >
          <div className="w-full max-w-6xl h-[92vh] bg-white rounded-[24px] shadow-2xl overflow-hidden flex flex-col">
            <div className="h-16 px-5 flex items-center gap-4 border-b border-slate-200 bg-white shrink-0">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-[#003B71] truncate">
                  Anteprima DDT {previewDdt.ddt || previewDdt.numero || `#${previewDdt.id}`}
                </div>
                <div className="text-xs text-slate-400 truncate">{previewDdt.fornitore || ""}</div>
              </div>
              <button
                type="button"
                onClick={() => setPreviewDdt(null)}
                className="w-11 h-11 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"
                title="Chiudi anteprima"
                aria-label="Chiudi anteprima"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <iframe
              title={`DDT ${previewDdt.ddt || previewDdt.numero || previewDdt.id}`}
              src={`/api/visualizza_ddt.php?ids=${encodeURIComponent(String(previewDdt.id))}&embed=1`}
              className="w-full flex-1 border-0 bg-slate-100"
            />
          </div>
        </div>
      )}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-8 space-y-6 md:space-y-8 bg-[#F6F8FA] min-h-screen">
        <div className="hidden md:block">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-400 hover:text-[#003B71] transition-colors group"
          >
            <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center group-hover:border-[#003B71]/30 shadow-sm transition-all">
              <ChevronLeft className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium">Indietro</span>
          </button>
        </div>

        <div id="main-title-area">
          <h2 className="text-3xl md:text-4xl text-slate-800 font-light tracking-tight">
            Registro Abbattimenti
          </h2>
          <p className="text-slate-400 text-sm mt-1 font-light">Compila il registro passo dopo passo.</p>
        </div>

        <Stepper
          steps={steps}
          step={step}
          setStep={setStep}
          stepperRef={stepperRef}
          mobileProgress={mobileProgress}
        />

        <Card>
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
              className="h-full flex flex-col"
            >
              {step === 0 && (
                <>
                  <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <span className="bg-blue-50 text-[#003B71] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                      1
                    </span>
                    Che trattamento vuoi eseguire?
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <OptionCard
                      active={form.trattamento === "congelare"}
                      onClick={() => setForm((f) => ({ ...f, trattamento: "congelare" }))}
                      title="Congelare"
                      desc="Trattamento a -18°C o inferiore."
                      icon={<Snowflake className="w-7 h-7" />}
                    />
                    <OptionCard
                      active={form.trattamento === "refrigerare"}
                      onClick={() => setForm((f) => ({ ...f, trattamento: "refrigerare" }))}
                      title="Refrigerare"
                      desc="Trattamento a +0/+4°C."
                      activeColor="green"
                      icon={<Thermometer className="w-7 h-7" />}
                    />
                  </div>

                  <div className="mb-2 bg-slate-50 p-4 rounded-[20px] border border-slate-100">
                    <FieldLabel>Data operazione</FieldLabel>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                      <Input
                        type="date"
                        className="pl-12 text-lg font-bold"
                        value={form.data_abbattimento}
                        onChange={(e) => setForm((f) => ({ ...f, data_abbattimento: e.target.value }))}
                      />
                    </div>
                  </div>
                </>
              )}

              {step === 1 && (
                <>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <span className="bg-blue-50 text-[#003B71] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                          2
                        </span>
                        Seleziona documenti origine
                      </h3>
                      <p className="text-sm text-slate-400 mt-1 ml-10">
                        Seleziona DDT, carica foto etichette, oppure entrambi.
                      </p>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <span className="text-xs font-bold bg-[#F0F9FF] text-[#003B71] px-4 py-1.5 rounded-full border border-blue-100">
                        DDT: {form.document_ids.length}
                      </span>
                      <span className="text-xs font-bold bg-fuchsia-50 text-fuchsia-700 px-4 py-1.5 rounded-full border border-fuchsia-100">
                        Foto: {form.label_photos.length}
                      </span>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-3 mb-5">
                    {[
                      { key: "both", label: "DDT + Foto" },
                      { key: "ddt", label: "Solo DDT" },
                      { key: "photos", label: "Solo Foto etichette" },
                    ].map((m) => (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, source_mode: m.key }))}
                        className={cn(
                          "px-4 py-3 rounded-[18px] border-2 text-sm font-bold transition-all",
                          form.source_mode === m.key
                            ? "border-[#003B71] bg-[#F0F7FF] text-[#003B71]"
                            : "border-slate-200 bg-white text-slate-500 hover:border-[#003B71]/30"
                        )}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>

                  {(form.source_mode === "ddt" || form.source_mode === "both") && (
                    <>
                      <div className="relative mb-5">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <input
                          className="w-full pl-12 pr-4 py-3 bg-slate-50 border-0 rounded-[20px] text-sm focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all shadow-inner"
                          placeholder="Cerca per numero documento o fornitore..."
                          value={docQuery}
                          onChange={(e) => setDocQuery(e.target.value)}
                        />
                      </div>

                      {docLoading && <div className="text-sm text-slate-500 mb-4">Carico documenti…</div>}
                      {docError && <div className="text-sm text-red-600 mb-4">{docError}</div>}

                      <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1 mb-6">
                        {!docLoading && docRows.length === 0 && (
                          <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-100 rounded-[20px] bg-slate-50/50">
                            <p className="text-sm">Nessun documento trovato.</p>
                          </div>
                        )}

                        {docRows.map((r) => {
                          const id = Number(r.id);
                          const checked = form.document_ids.includes(id);
                          const title =
                            r.label ||
                            `${r.ddt || r.numero || "Documento"} — ${r.fornitore || ""} (${r.data || ""})`;

                          return (
                            <label
                              key={r.id}
                              className="flex items-center gap-4 p-4 rounded-[20px] border border-slate-100 bg-white hover:border-[#003B71]/30 hover:bg-[#F0F9FF] transition-all cursor-pointer group shadow-sm"
                            >
                              <div className="relative flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    setForm((f) => {
                                      const set = new Set(f.document_ids);
                                      if (e.target.checked) set.add(id);
                                      else set.delete(id);
                                      const document_ids = Array.from(set);
                                      const fornitori = docRows
                                        .filter((doc) => document_ids.includes(Number(doc.id)))
                                        .map((doc) => (doc.fornitore || "").trim())
                                        .filter((nome, index, all) => nome && all.indexOf(nome) === index);
                                      const fornitore = fornitori.join(", ");
                                      setFornQuery(fornitore);
                                      return { ...f, document_ids, fornitore, fornitore_id: "" };
                                    });
                                  }}
                                  className="peer appearance-none w-6 h-6 border-2 border-slate-300 rounded-md checked:bg-[#003B71] checked:border-[#003B71] transition-all"
                                />
                                <Check className="w-4 h-4 text-white absolute opacity-0 peer-checked:opacity-100 pointer-events-none" />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-center gap-3">
                                  <span className="font-bold text-slate-800 text-base md:text-lg truncate">{title}</span>
                                  <span className="text-xs font-bold bg-slate-100 text-slate-500 px-2 py-1 rounded-lg shrink-0">
                                    {r.data || "—"}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setPreviewDdt(r);
                                    }}
                                    className="w-9 h-9 rounded-xl bg-[#EEF2FF] text-[#003B71] flex items-center justify-center hover:bg-[#003B71] hover:text-white transition-colors shrink-0"
                                    title={`Anteprima DDT ${r.ddt || r.numero || `#${r.id}`}`}
                                    aria-label={`Anteprima DDT ${r.ddt || r.numero || `#${r.id}`}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </div>

                                <div className="text-xs text-slate-400 flex flex-wrap items-center gap-2 mt-1">
                                  <span className="flex items-center gap-1 bg-slate-50 px-1.5 py-0.5 rounded">
                                    <Hash className="w-3 h-3" />
                                    ID #{r.id}
                                  </span>
                                  {r.has_ddt_file ? (
                                    <span className="flex items-center gap-1">
                                      <File className="w-3 h-3" />
                                      file
                                    </span>
                                  ) : null}
                                  {r.has_etichette ? (
                                    <span className="flex items-center gap-1">
                                      <FileText className="w-3 h-3" />
                                      immagini
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {(form.source_mode === "photos" || form.source_mode === "both") && (
                    <>
                      <div className="border-t border-slate-100 pt-6">
                        <div className="flex items-start gap-4 bg-fuchsia-50/60 p-5 rounded-[24px] mb-5 border border-fuchsia-100">
                          <div className="w-10 h-10 rounded-full bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center shrink-0">
                            <Info className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-fuchsia-700 text-sm mb-1">Etichette materie prime</h4>
                            <p className="text-sm text-fuchsia-700/80 leading-relaxed">
                              Puoi fotografare le etichette delle materie prime usate. Le immagini verranno salvate
                              nella cartella dedicata dell’abbattimento subito dopo il salvataggio.
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-3 mb-5">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-[18px] bg-[#003B71] text-white shadow hover:bg-[#002B5B]"
                          >
                            <Upload className="w-4 h-4" />
                            Carica foto
                          </button>

                          <button
                            type="button"
                            onClick={() => cameraInputRef.current?.click()}
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-[18px] bg-white border border-slate-200 text-slate-700 shadow-sm hover:bg-slate-50"
                          >
                            <Camera className="w-4 h-4" />
                            Scatta foto
                          </button>

                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              addPhotoFiles(e.target.files);
                              e.target.value = "";
                            }}
                          />

                          <input
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              addPhotoFiles(e.target.files);
                              e.target.value = "";
                            }}
                          />
                        </div>

                        {form.label_photos.length === 0 ? (
                          <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-100 rounded-[20px] bg-slate-50/50">
                            <p className="text-sm">Nessuna foto caricata.</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {form.label_photos.map((img) => (
                              <div
                                key={img.id}
                                className="rounded-[20px] border border-slate-100 bg-white p-3 shadow-sm"
                              >
                                <div className="aspect-square rounded-[16px] overflow-hidden bg-slate-100 mb-2">
                                  {img.preview ? (
                                    <img
                                      src={img.preview}
                                      alt={img.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full grid place-items-center text-slate-400 text-xs">
                                      Nessuna anteprima
                                    </div>
                                  )}
                                </div>

                                <div className="text-xs text-slate-500 truncate mb-2">{img.name}</div>

                                <button
                                  type="button"
                                  onClick={() => removePhoto(img.id)}
                                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-[14px] text-red-600 border border-red-100 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Rimuovi
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <p className="text-xs text-slate-400 mt-4 italic flex items-center gap-1">
                    <ChevronRight className="w-3 h-3" />
                    In modalità “DDT + Foto” basta almeno uno dei due per proseguire.
                  </p>
                </>
              )}

              {step === 2 && (
                <>
                  <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <span className="bg-blue-50 text-[#003B71] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                      3
                    </span>
                    Specifica il Fornitore
                  </h3>

                  <div className="bg-blue-50/50 p-5 rounded-[24px] mb-8 border border-blue-100 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 text-[#003B71] flex items-center justify-center shrink-0">
                      <Info className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#003B71] text-sm mb-1">Importazione automatica</h4>
                      <p className="text-sm text-[#003B71]/70 leading-relaxed">
                        I fornitori dei DDT selezionati sono riportati automaticamente. Puoi comunque modificarli
                        manualmente.
                      </p>
                    </div>
                  </div>

                  <div className="flex-1 relative">
                    <FieldLabel>Nome fornitore</FieldLabel>
                    <div className="relative group">
                      <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                      <Input
                        className="pl-12 py-4 text-lg"
                        placeholder="Es. Marr, Gold Caffè..."
                        value={fornQuery}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFornQuery(v);
                          setForm((f) => ({ ...f, fornitore: v, fornitore_id: "" }));
                        }}
                        onFocus={() => {
                          if (fornItems.length > 0) setFornOpen(true);
                        }}
                        onBlur={() => setTimeout(() => setFornOpen(false), 150)}
                      />
                    </div>

                    {fornLoading && <div className="text-xs text-slate-500 mt-2">Cerco fornitori…</div>}

                    {fornOpen && fornItems.length > 0 && (
                      <div className="absolute z-20 mt-2 w-full bg-white border border-slate-200 rounded-[18px] shadow-lg overflow-hidden">
                        {fornItems.slice(0, 10).map((it) => (
                          <button
                            key={it.id}
                            type="button"
                            className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              const name = it.nome || "";
                              setForm((f) => ({ ...f, fornitore: name, fornitore_id: String(it.id || "") }));
                              setFornQuery(name);
                              setFornOpen(false);
                            }}
                          >
                            <div className="font-medium text-slate-800">{it.nome}</div>
                            <div className="text-xs text-slate-500">ID #{it.id}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                    <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                      <span className="bg-blue-50 text-[#003B71] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                        4
                      </span>
                      Prodotti Abbattuti
                    </h3>

                    <button
                      type="button"
                      className="px-6 py-2.5 bg-[#003B71] hover:bg-[#002B5B] text-white text-sm font-bold rounded-[14px] flex items-center gap-2 shadow-md transition-all active:scale-95"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          prodotti: [...f.prodotti, { nome: "", quantita: "", note: "" }],
                        }))
                      }
                    >
                      + Aggiungi prodotto
                    </button>
                  </div>

                  <div className="space-y-4">
                    {form.prodotti.map((p, idx) => (
                      <div
                        key={idx}
                        className="bg-slate-50/80 p-5 rounded-[24px] border border-slate-100"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="md:col-span-2">
                            <FieldLabel>Nome prodotto</FieldLabel>
                            <Input
                              value={p.nome}
                              placeholder="Es. Baccalà, Pollo, ecc."
                              onChange={(e) =>
                                setForm((f) => {
                                  const next = [...f.prodotti];
                                  next[idx] = { ...next[idx], nome: e.target.value };
                                  return { ...f, prodotti: next };
                                })
                              }
                            />
                          </div>

                          <div>
                            <FieldLabel>Quantità</FieldLabel>
                            <Input
                              value={p.quantita}
                              placeholder="Es. 3 kg"
                              onChange={(e) =>
                                setForm((f) => {
                                  const next = [...f.prodotti];
                                  next[idx] = { ...next[idx], quantita: e.target.value };
                                  return { ...f, prodotti: next };
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="mb-4">
                          <FieldLabel>Note (opzionale)</FieldLabel>
                          <Input
                            value={p.note}
                            placeholder="Dettagli aggiuntivi..."
                            onChange={(e) =>
                              setForm((f) => {
                                const next = [...f.prodotti];
                                next[idx] = { ...next[idx], note: e.target.value };
                                return { ...f, prodotti: next };
                              })
                            }
                          />
                        </div>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() =>
                              setForm((f) => {
                                const next = f.prodotti.filter((_, i) => i !== idx);
                                return {
                                  ...f,
                                  prodotti: next.length ? next : [{ nome: "", quantita: "", note: "" }],
                                };
                              })
                            }
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                </>
              )}

              {step === 4 && (
                <div className="h-full flex flex-col justify-center">
                  <div className="text-center mb-8">
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Numero Lotto</h3>
                  </div>

                  <div className="relative max-w-lg mx-auto w-full">
                    <FieldLabel>Codice lotto</FieldLabel>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Hash className="w-5 h-5 text-slate-400" />
                      </div>

                      <Input
                        className="pl-12 pr-28 py-5 bg-slate-50 font-bold font-mono text-xl rounded-[20px]"
                        value={form.lotto}
                        onChange={(e) => setForm((f) => ({ ...f, lotto: e.target.value }))}
                      />

                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, lotto: autoLotto() }))}
                        className="absolute right-2 top-2 bottom-2 px-4 rounded-[14px] bg-white border border-slate-200 text-xs font-bold inline-flex items-center gap-2"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Rigenera
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {step === 5 && (
                <>
                  <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <span className="bg-blue-50 text-[#003B71] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                      6
                    </span>
                    Parametri di Avvio
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <FieldLabel>Inizio</FieldLabel>
                      <Input
                        type="datetime-local"
                        className="py-4 rounded-[20px]"
                        value={isoToLocalDatetimeValue(form.inizio_iso)}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, inizio_iso: localDatetimeValueToIso(e.target.value) }))
                        }
                      />
                    </div>

                    <div>
                      <FieldLabel>Temperatura Inizio (°C)</FieldLabel>
                      <Input
                        type="number"
                        step="0.1"
                        className="py-4 rounded-[20px]"
                        placeholder="Es. 4.0"
                        value={form.temp_inizio}
                        onChange={(e) => setForm((f) => ({ ...f, temp_inizio: e.target.value }))}
                      />
                      <div className="mt-3 flex flex-wrap gap-2">
                        {TEMP_PRESETS.map((preset) => (
                          <button
                            key={preset.value}
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, temp_inizio: preset.value }))}
                            className={cn(
                              "rounded-full border px-3 py-1.5 text-xs font-bold transition-all",
                              String(form.temp_inizio) === preset.value
                                ? "border-[#003B71] bg-[#003B71] text-white shadow-sm"
                                : "border-slate-200 bg-white text-slate-600 hover:border-[#003B71]/40 hover:text-[#003B71]"
                            )}
                          >
                            {preset.label} {preset.value}{"\u00b0C"}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mb-8 rounded-[24px] border border-emerald-100 bg-emerald-50/60 p-5">
                    <FieldLabel>Data di scadenza del prodotto abbattuto (opzionale)</FieldLabel>
                    <div className="relative max-w-md">
                      <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600 w-5 h-5" />
                      <Input
                        type="date"
                        min={form.data_abbattimento || undefined}
                        className="pl-12 py-4 rounded-[20px]"
                        value={form.data_scadenza_prodotto}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, data_scadenza_prodotto: e.target.value }))
                        }
                      />
                    </div>
                    <p className="mt-2 text-xs text-emerald-800/70">
                      La data sarà salvata con l'abbattimento e stampata sull'etichetta del prodotto.
                    </p>
                  </div>

                  <div className="mb-8 rounded-[24px] border border-cyan-100 bg-cyan-50/60 p-5">
                    <div className="flex flex-col md:flex-row md:items-end gap-4">
                      <div className="flex-1">
                        <FieldLabel>Cryo a cuore (opzionale)</FieldLabel>
                        <select
                          className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-[16px] text-slate-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71]/20 focus:border-[#003B71] transition-all"
                          value={form.cryo_device_id}
                          onChange={(e) => {
                            const device = cryoDevices.find((d) => String(d.device_id) === e.target.value);
                            setForm((f) => ({
                              ...f,
                              cryo_device_id: e.target.value,
                              cryo_mac_address: device?.mac_address || "",
                              cryo_snapshot_start: null,
                            }));
                          }}
                        >
                          <option value="">Nessuna sonda collegata</option>
                          {cryoDevices.map((device) => (
                            <option key={device.device_id} value={device.device_id}>
                              {device.nome || device.mac_address}
                            </option>
                          ))}
                        </select>
                        {cryoLoading && <p className="mt-2 text-xs text-slate-500">Lettura Cryo in corso...</p>}
                        {cryoError && <p className="mt-2 text-xs text-red-600">{cryoError}</p>}
                      </div>

                      <button
                        type="button"
                        disabled={!form.cryo_device_id || cryoLoading}
                        onClick={() => captureCryoStart(form.cryo_device_id)}
                        className="inline-flex items-center justify-center gap-2 rounded-[16px] bg-cyan-700 px-4 py-3 text-sm font-bold text-white hover:bg-cyan-800 disabled:opacity-50"
                      >
                        <Thermometer className="w-4 h-4" />
                        Cattura inizio
                      </button>
                    </div>

                    {form.cryo_device_id && (
                      <label className="mt-4 flex items-center gap-2 text-sm font-medium text-slate-700">
                        <input
                          type="checkbox"
                          checked={!!form.cryo_monitoring_enabled}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, cryo_monitoring_enabled: e.target.checked }))
                          }
                        />
                        Monitora durante l'abbattimento
                      </label>
                    )}

                    <SnapshotPreview snapshot={form.cryo_snapshot_start} />
                  </div>

                  <div className="bg-[#F0F9FF] border-2 border-blue-100 rounded-[24px] p-6 flex items-center gap-5 shadow-inner">
                    <div className="w-12 h-12 rounded-full bg-blue-200 text-[#003B71] flex items-center justify-center shrink-0">
                      <Timer className="w-6 h-6" />
                    </div>

                    <div>
                      <span className="block text-xs font-bold text-[#003B71] uppercase tracking-wide mb-1">
                        Scadenza completamento
                      </span>
                      <div className="text-2xl font-bold text-slate-800 font-mono">
                        {fmtDeadline(form.inizio_iso, form.trattamento)}
                      </div>
                      <p className="text-xs text-blue-400 mt-1">
                        Tempo massimo: {form.trattamento === "congelare" ? "4 ore" : "2 ore"}.
                      </p>
                    </div>
                  </div>
                </>
              )}

              {step === 6 && (
                <>
                  <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <span className="bg-blue-50 text-[#003B71] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                      7
                    </span>
                    Seleziona Abbattitore
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <label className="relative group cursor-pointer">
                      <div className="p-6 rounded-[20px] border-2 border-slate-100 bg-[#F0F7FF] flex items-center gap-4">
                        <Zap className="w-6 h-6 text-[#003B71]" />
                        <h4 className="font-semibold text-slate-800">Abbattitore cucina</h4>
                      </div>
                    </label>
                  </div>

                  <div className="mt-4 border-t border-slate-100 pt-6">
                    <FieldLabel>Oppure inserisci ID manuale</FieldLabel>
                    <div className="relative">
                      <Zap className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                      <Input
                        className="pl-12 py-4 bg-slate-50 rounded-[20px]"
                        placeholder="ID Abbattitore (opzionale)"
                        value={form.abbattitore_id}
                        onChange={(e) => setForm((f) => ({ ...f, abbattitore_id: e.target.value }))}
                      />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2 ml-1">
                      In futuro questa selezione sarà automatica tramite sonde IoT.
                    </p>
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </Card>

        <div className="flex items-center justify-between mt-6">
          <button
            type="button"
            onClick={handleBack}
            disabled={step === 0 || loading}
            className={cn(
              "px-6 py-3 rounded-[20px] text-sm font-bold text-slate-500 hover:bg-white hover:text-slate-800 hover:shadow-sm transition-all flex items-center gap-2 border border-transparent hover:border-slate-200",
              (step === 0 || loading) && "opacity-40 pointer-events-none"
            )}
          >
            <ChevronLeft className="w-4 h-4" />
            Indietro
          </button>

          <div className="flex gap-3 ml-auto w-full md:w-auto justify-end">
            <button
              type="button"
              onClick={close}
              className="px-6 py-3 rounded-[20px] text-sm font-bold text-slate-500 hover:bg-white hover:text-red-500 hover:shadow-sm transition-all hidden md:block border border-transparent hover:border-red-100"
            >
              Chiudi
            </button>

            <button
              type="button"
              onClick={handleNext}
              disabled={!validateCurrent() || loading}
              className="flex-1 md:flex-none px-8 py-3 rounded-[20px] text-sm font-bold bg-[#003B71] text-white shadow-lg hover:shadow-xl hover:bg-[#002B5B] transition-all flex items-center justify-center gap-2 active:scale-[0.99] disabled:opacity-50"
            >
              {step === steps.length - 1 ? (
                <>
                  {loading ? "Salvataggio..." : "Salva"}
                  <Save className="w-4 h-4" />
                </>
              ) : (
                <>
                  Avanti
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Page>
  );
}
